/*
  The Chart Room — Chart Cast.
  ----------------------------
  Computes the four pillars (四柱) of a BaZi chart ENTIRELY IN THE BROWSER.

  The privacy claim on the page — "computed in your browser; your birth details
  never leave your device" — is a claim about this file. It is true only while
  all of the following hold:

    * this module makes NO network call. There is no fetch, no XHR, no
      sendBeacon, no image ping, no dynamic import, no external CDN. The solar
      term table is baked in below precisely so there is nothing to fetch.
    * it writes NO storage. No localStorage, no sessionStorage, no cookie, no
      IndexedDB, no cache.
    * it emits NO analytics. It does not touch dataLayer, gtag, fbq, or
      Shopify's analytics object, and the form it drives never submits.

  Anything added to this file or to the page that breaks one of those three
  makes the sentence on the page false. See README.md § Chart Cast.

  CONVENTIONS. Every rule below is frozen to match the verified Python engine at
  Chinese-Metaphysics-KB/tools/bazi-calc/bazi_cli.py under its shipped
  default_config.json. Where a school disagrees, this file follows the engine,
  not a judgement call made here. dev/test_chart_cast.mjs asserts the match
  against engine output for a fixed battery of cases.

    year_boundary   立春. The year pillar turns at the exact 立春 instant of the
                    UTC+8 calendar year containing the birth, never at 正月初一.
    month_boundary  节. The month branch turns at the twelve 节, never at a
                    new moon and never at a 中气.
    year + month    are decided by comparing the ABSOLUTE UTC INSTANT of birth
                    against the absolute UTC instant of the term. This is why
                    the timezone selector matters at all.
    day_boundary    zi_split — 子初换日. The day pillar advances at 23:00 local
                    clock time, the start of 子时.
    day pillar      (JDN + 49) mod 60 on that boundary-adjusted date.
    hour_pillar     五鼠遁 from the day stem; branch = floor((hour + 1) / 2) mod 12.
    hour basis      civil. Day and hour pillars are read off the LOCAL CLOCK
                    TIME as entered, not off true solar time. A local clock is
                    what the convention has always read.
    hidden stems    渊海子平 地支藏遁歌, 主气 first then 余气, no 司令分日 weighting.
    ten gods        five-phase relation + yin/yang match against the day stem.

  Out of scope on purpose: 旺衰, 格局, 用神, 神煞, luck-cycle dates. This module
  produces the mechanical chart and nothing that could be mistaken for a reading.
*/

/* ------------------------------------------------------------------ tables */

const STEMS = '甲乙丙丁戊己庚辛壬癸';
const BRANCHES = '子丑寅卯辰巳午未申酉戌亥';

const ELEMENTS = ['木', '火', '土', '金', '水'];
const ELEMENT_EN = { 木: 'Wood', 火: 'Fire', 土: 'Earth', 金: 'Metal', 水: 'Water' };
const ELEMENT_KEY = { 木: 'wood', 火: 'fire', 土: 'earth', 金: 'metal', 水: 'water' };

// stem index // 2 -> element; the 生 cycle runs 木火土金水.
const GENERATES = [1, 2, 3, 4, 0];
const CONTROLS = [2, 3, 4, 0, 1];

// Branch elements, in 子丑寅卯辰巳午未申酉戌亥 order.
const BRANCH_ELEMENTS = ['水', '土', '木', '木', '土', '火', '火', '土', '金', '金', '土', '水'];

// 渊海子平 地支藏遁歌 — 主气 first, then 余气.
const HIDDEN_STEMS = {
  子: ['癸'],
  丑: ['己', '癸', '辛'],
  寅: ['甲', '丙', '戊'],
  卯: ['乙'],
  辰: ['戊', '乙', '癸'],
  巳: ['丙', '戊', '庚'],
  午: ['丁', '己'],
  未: ['己', '丁', '乙'],
  申: ['庚', '壬', '戊'],
  酉: ['辛'],
  戌: ['戊', '辛', '丁'],
  亥: ['壬', '甲'],
};

// 二十四节气 in sxtwl jqIndex order, starting at 冬至. Odd indexes are the 节.
const JIEQI_NAMES = [
  '冬至', '小寒', '大寒', '立春', '雨水', '惊蛰',
  '春分', '清明', '谷雨', '立夏', '小满', '芒种',
  '夏至', '小暑', '大暑', '立秋', '处暑', '白露',
  '秋分', '寒露', '霜降', '立冬', '小雪', '大雪',
];

const TEN_GOD_EN = {
  比肩: 'Peer',
  劫财: 'Rival',
  食神: 'Output (gentle)',
  伤官: 'Output (sharp)',
  偏财: 'Wealth (indirect)',
  正财: 'Wealth (direct)',
  七杀: 'Authority (sharp)',
  正官: 'Authority (direct)',
  偏印: 'Support (indirect)',
  正印: 'Support (direct)',
  日主: 'Day Master',
};

/* ------------------------------------------------- solar term table (节) */

/*
  Generated, not hand-written. Run:

      <bazi-calc venv python> dev/gen_solar_terms.py

  which reads the tested engine's own sxtwl-backed 节 table and rewrites the
  block between the markers below. Do not edit the literal by hand.

  Encoding: `epoch` is the whole-minute UTC time of the first 节, counted from
  1900-01-01T00:00Z and rounded UP; `deltas` holds the gap to each following
  term as a fixed-width 4-character base36 number of minutes. Rounding terms UP
  to the minute makes the integer test `birthMinute >= termMinute` exactly
  equivalent to the engine's instant comparison, because the form only accepts
  whole minutes and no 节 in the table falls on :00 seconds.
*/
/* SOLAR-TERMS-TABLE:BEGIN */
const TERM_TABLE = {
  epoch: 20518888,
  firstJq: 1,
  count: 1116,
  deltas:
    '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',
};
/* SOLAR-TERMS-TABLE:END */

let TERM_MINUTES = null;

function termMinutes() {
  if (TERM_MINUTES) return TERM_MINUTES;
  const out = new Array(TERM_TABLE.count);
  let t = TERM_TABLE.epoch;
  out[0] = t;
  for (let i = 1; i < TERM_TABLE.count; i += 1) {
    t += parseInt(TERM_TABLE.deltas.slice((i - 1) * 4, i * 4), 36);
    out[i] = t;
  }
  TERM_MINUTES = out;
  return out;
}

function termJqIndex(i) {
  return (TERM_TABLE.firstJq + 2 * i) % 24;
}

/** Branch of the month a 节 opens. 立春 (jqIndex 3) opens 寅, one branch per 节. */
function termMonthBranchIndex(jq) {
  return (Math.floor((jq - 3) / 2) + 2 + 12) % 12;
}

/** The UTC+8 calendar year a term falls in — the engine indexes 立春 by this. */
function termCstYear(minutes) {
  return minutesToUtcParts(minutes + 480).year;
}

export const TERM_TABLE_RANGE = Object.freeze({
  get firstYear() {
    return termCstYear(termMinutes()[0]);
  },
  get lastYear() {
    return termCstYear(termMinutes()[TERM_TABLE.count - 1]);
  },
});

/* ------------------------------------------------------------- primitives */

const MIN_EPOCH_MS = Date.UTC(1900, 0, 1);

function minutesToUtcParts(minutes) {
  const d = new Date(MIN_EPOCH_MS + minutes * 60000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

/** Gregorian Julian Day Number. Same integer arithmetic as the engine. */
export function jdn(year, month, day) {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

function ganzhi(index) {
  return STEMS[((index % 10) + 10) % 10] + BRANCHES[((index % 12) + 12) % 12];
}

/** Sexagenary index from a stem index and a branch index. */
function combine(stemIndex, branchIndex) {
  for (let i = 0; i < 60; i += 1) {
    if (i % 10 === stemIndex && i % 12 === branchIndex) return i;
  }
  throw new Error(`impossible stem/branch pair ${stemIndex}/${branchIndex}`);
}

function stemElement(stem) {
  return ELEMENTS[Math.floor(STEMS.indexOf(stem) / 2)];
}

function stemIsYang(stem) {
  return STEMS.indexOf(stem) % 2 === 0;
}

/** 十神 of `other` relative to the day master. */
export function tenGod(dayMaster, other) {
  const dm = STEMS.indexOf(dayMaster);
  const ot = STEMS.indexOf(other);
  const dmE = Math.floor(dm / 2);
  const otE = Math.floor(ot / 2);
  const samePolarity = dm % 2 === ot % 2;
  if (dmE === otE) return samePolarity ? '比肩' : '劫财';
  if (GENERATES[dmE] === otE) return samePolarity ? '食神' : '伤官';
  if (CONTROLS[dmE] === otE) return samePolarity ? '偏财' : '正财';
  if (CONTROLS[otE] === dmE) return samePolarity ? '七杀' : '正官';
  return samePolarity ? '偏印' : '正印';
}

/* ---------------------------------------------------------------- timezone */

/*
  The ONLY place the timezone enters the chart is the conversion of the entered
  wall-clock time into an absolute instant, which decides which side of a 节 or
  of 立春 the birth falls on. Day and hour pillars are read straight off the
  entered clock (hour_pillar_basis = civil), so for the overwhelming majority of
  births the timezone changes nothing at all.

  Historical offsets matter here and the browser already has them: Intl carries
  the IANA database, including Singapore's UTC+7:30 before 1 Jan 1982. FALLBACK
  offsets are declared per zone for the case where Intl refuses the zone; they
  are today's standard offsets and are only ever reached on a browser too old
  to know the zone at all.
*/

export const TIMEZONES = [
  { id: 'Asia/Singapore', label: 'Singapore', fallback: 480 },
  { id: 'Asia/Kuala_Lumpur', label: 'Malaysia', fallback: 480 },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong', fallback: 480 },
  { id: 'Asia/Shanghai', label: 'China (mainland)', fallback: 480 },
  { id: 'Asia/Taipei', label: 'Taiwan', fallback: 480 },
  { id: 'Asia/Jakarta', label: 'Indonesia (Jakarta)', fallback: 420 },
  { id: 'Asia/Bangkok', label: 'Thailand', fallback: 420 },
  { id: 'Asia/Manila', label: 'Philippines', fallback: 480 },
  { id: 'Asia/Tokyo', label: 'Japan', fallback: 540 },
  { id: 'Asia/Seoul', label: 'South Korea', fallback: 540 },
  { id: 'Asia/Kolkata', label: 'India', fallback: 330 },
  { id: 'Australia/Sydney', label: 'Australia (Sydney)', fallback: 600 },
  { id: 'Europe/London', label: 'United Kingdom', fallback: 0 },
  { id: 'America/New_York', label: 'United States (New York)', fallback: -300 },
  { id: 'America/Los_Angeles', label: 'United States (Los Angeles)', fallback: -480 },
];

const FORMATTERS = new Map();

function formatterFor(zone) {
  if (FORMATTERS.has(zone)) return FORMATTERS.get(zone);
  let f = null;
  try {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      era: 'short',
    });
    f.format(new Date());
  } catch (e) {
    f = null;
  }
  FORMATTERS.set(zone, f);
  return f;
}

function fallbackOffset(zone) {
  const found = TIMEZONES.find((t) => t.id === zone);
  return found ? found.fallback : 0;
}

/** Offset in minutes east of UTC that `zone` was on at absolute time `ms`. */
function zoneOffsetAt(ms, zone) {
  const f = formatterFor(zone);
  if (!f) return fallbackOffset(zone);
  const parts = {};
  for (const p of f.formatToParts(new Date(ms))) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute)
  );
  return Math.round((asUtc - ms) / 60000);
}

/**
 * Absolute instant, in minutes since 1900-01-01T00:00Z, of a wall-clock time in
 * `zone`. Two passes: guess with the offset that applies at the naive instant,
 * then re-read the offset at the corrected instant. That settles every case
 * except a wall time inside a DST gap, where the second pass lands on the
 * offset after the transition — the same resolution Python's zoneinfo picks.
 */
export function wallClockToMinutes(year, month, day, hour, minute, zone) {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  let ms = naive - zoneOffsetAt(naive, zone) * 60000;
  ms = naive - zoneOffsetAt(ms, zone) * 60000;
  return Math.round((ms - MIN_EPOCH_MS) / 60000);
}

/* -------------------------------------------------------------- the chart */

function pillarBlock(index, dayMaster, label) {
  const stem = STEMS[index % 10];
  const branch = BRANCHES[index % 12];
  const hidden = HIDDEN_STEMS[branch];
  const branchIndex = BRANCHES.indexOf(branch);
  return {
    label,
    ganzhi: stem + branch,
    stem,
    stemElement: stemElement(stem),
    stemElementKey: ELEMENT_KEY[stemElement(stem)],
    stemElementEn: ELEMENT_EN[stemElement(stem)],
    stemYinYang: stemIsYang(stem) ? '阳' : '阴',
    stemTenGod: label === 'day' ? '日主' : tenGod(dayMaster, stem),
    stemTenGodEn: label === 'day' ? TEN_GOD_EN['日主'] : TEN_GOD_EN[tenGod(dayMaster, stem)],
    branch,
    branchElement: BRANCH_ELEMENTS[branchIndex],
    branchElementKey: ELEMENT_KEY[BRANCH_ELEMENTS[branchIndex]],
    branchElementEn: ELEMENT_EN[BRANCH_ELEMENTS[branchIndex]],
    branchYinYang: branchIndex % 2 === 0 ? '阳' : '阴',
    hiddenStems: hidden.map((h) => ({
      stem: h,
      element: stemElement(h),
      elementKey: ELEMENT_KEY[stemElement(h)],
      tenGod: tenGod(dayMaster, h),
      tenGodEn: TEN_GOD_EN[tenGod(dayMaster, h)],
    })),
    sexagenaryIndex: index,
  };
}

/**
 * The four pillars for one birth.
 *
 * @param {object} input
 * @param {string} input.date      YYYY-MM-DD, Gregorian
 * @param {string} input.time      HH:MM, local clock in `timezone`
 * @param {string} [input.timezone] IANA zone id, default Asia/Singapore
 * @param {string} [input.sex]     'M' | 'F' | '' — used only for the luck note
 * @param {number} [input.shiftMinutes] displaces the birth time; used for the
 *                                 A/B charts shown when the hour is unsure
 */
export function computeChart(input) {
  const zone = input.timezone || 'Asia/Singapore';
  const [y, mo, d] = String(input.date).split('-').map(Number);
  const [rawHh, rawMm] = String(input.time).split(':').map(Number);

  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    throw new Error('a birth date is needed');
  }
  if (!Number.isFinite(rawHh) || !Number.isFinite(rawMm)) {
    throw new Error('a birth time is needed');
  }

  // A shift is applied on the local clock, so a displaced chart stays a chart of
  // a clock reading — which is what the hour convention consumes.
  const shift = Number(input.shiftMinutes) || 0;
  const baseJdn = jdn(y, mo, d);
  const totalMinutes = rawHh * 60 + rawMm + shift;
  const dayCarry = Math.floor(totalMinutes / 1440);
  const minuteOfDay = ((totalMinutes % 1440) + 1440) % 1440;
  const hh = Math.floor(minuteOfDay / 60);
  const mm = minuteOfDay % 60;
  const localJdn = baseJdn + dayCarry;
  const localDate = fromJdn(localJdn);

  const terms = termMinutes();
  const birthMinutes = wallClockToMinutes(
    localDate.year,
    localDate.month,
    localDate.day,
    hh,
    mm,
    zone
  );

  // --- year: the 立春 instant of the UTC+8 calendar year containing the birth
  const cstYear = termCstYear(birthMinutes);

  // The table is padded a year either side of the supported range so that both
  // the governing 节 and the following 节 always exist for a supported birth.
  const firstSupported = TERM_TABLE_RANGE.firstYear + 1;
  const lastSupported = TERM_TABLE_RANGE.lastYear - 1;
  if (cstYear < firstSupported || cstYear > lastSupported) {
    throw new Error(
      `This page covers births from ${firstSupported} to ${lastSupported}. ` +
        'Outside that range the chart is still computable — it just needs the full tool.'
    );
  }

  let lichunIdx = -1;
  for (let i = 0; i < terms.length; i += 1) {
    if (termJqIndex(i) === 3 && termCstYear(terms[i]) === cstYear) {
      lichunIdx = i;
      break;
    }
  }
  if (lichunIdx < 0) throw new Error(`立春 for ${cstYear} is outside the table`);
  const solarYear = birthMinutes >= terms[lichunIdx] ? cstYear : cstYear - 1;
  const yearIndex = (((solarYear - 4) % 60) + 60) % 60;

  // --- month: the governing 节
  let jieIdx = -1;
  for (let i = 0; i < terms.length; i += 1) {
    if (terms[i] <= birthMinutes) jieIdx = i;
    else break;
  }
  const monthBranchIndex = termMonthBranchIndex(termJqIndex(jieIdx));
  const stepsFromYin = (monthBranchIndex - 2 + 12) % 12; // 寅 is the first month
  const monthStemIndex = ((yearIndex % 10) * 2 + 2 + stepsFromYin) % 10;
  const monthIndex = combine(monthStemIndex, monthBranchIndex);

  // --- day: 子初换日 — the day advances at 23:00 local clock time
  const dayJdn = localJdn + (hh >= 23 ? 1 : 0);
  const dayIndex = (((dayJdn + 49) % 60) + 60) % 60;

  // --- hour: 五鼠遁 from the day stem
  const hourBranchIndex = Math.floor((hh + 1) / 2) % 12;
  const hourStemIndex = ((dayIndex % 10) * 2 + hourBranchIndex) % 10;
  const hourIndex = combine(hourStemIndex, hourBranchIndex);

  const dayMaster = STEMS[dayIndex % 10];
  const pillars = {
    year: pillarBlock(yearIndex, dayMaster, 'year'),
    month: pillarBlock(monthIndex, dayMaster, 'month'),
    day: pillarBlock(dayIndex, dayMaster, 'day'),
    hour: pillarBlock(hourIndex, dayMaster, 'hour'),
  };

  return {
    eightCharacters: [yearIndex, monthIndex, dayIndex, hourIndex].map(ganzhi).join(' '),
    dayMaster,
    dayMasterElement: stemElement(dayMaster),
    dayMasterElementKey: ELEMENT_KEY[stemElement(dayMaster)],
    dayMasterElementEn: ELEMENT_EN[stemElement(dayMaster)],
    dayMasterYinYang: stemIsYang(dayMaster) ? '阳' : '阴',
    pillars,
    order: ['year', 'month', 'day', 'hour'],
    monthCommand: {
      branch: BRANCHES[monthIndex % 12],
      governingJie: JIEQI_NAMES[termJqIndex(jieIdx)],
      nextJie: JIEQI_NAMES[termJqIndex(jieIdx + 1)],
    },
    luck: luckDirection(yearIndex, input.sex),
    clock: {
      date: `${pad(localDate.year, 4)}-${pad(localDate.month, 2)}-${pad(localDate.day, 2)}`,
      time: `${pad(hh, 2)}:${pad(mm, 2)}`,
      timezone: zone,
      dayRolled: hh >= 23,
    },
  };
}

function pad(n, w) {
  return String(n).padStart(w, '0');
}

/** Inverse of `jdn`, for the rare case a shift crosses midnight. */
function fromJdn(j) {
  let a = j + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const dd = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * dd) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  return {
    day: e - Math.floor((153 * m + 2) / 5) + 1,
    month: m + 3 - 12 * Math.floor(m / 10),
    year: 100 * b + dd - 4800 + Math.floor(m / 10),
  };
}

/*
  Luck-cycle DIRECTION only — 阳年男 / 阴年女 run forward, 阴年男 / 阳年女 run
  backward. The direction is a one-line mechanical fact. The dates the cycles
  open, and anything about what they mean, are not computed here: the 起运
  conversion is school-divergent and belongs in a read chart, not a free page.
*/
function luckDirection(yearIndex, sex) {
  const yearStem = STEMS[yearIndex % 10];
  const yang = stemIsYang(yearStem);
  if (sex !== 'M' && sex !== 'F') {
    return { known: false, yearStem, yearStemPolarity: yang ? '阳' : '阴' };
  }
  const forward = (yang && sex === 'M') || (!yang && sex === 'F');
  return {
    known: true,
    forward,
    yearStem,
    yearStemPolarity: yang ? '阳' : '阴',
    sex,
  };
}

/**
 * Notes that must travel with a chart. Every one of these is a statement about
 * the input, not about the person.
 */
export function chartNotes(input, chart) {
  const notes = [];
  const zone = input.timezone || 'Asia/Singapore';
  const [y, mo, d] = String(input.date).split('-').map(Number);

  const preTz = zone === 'Asia/Singapore' || zone === 'Asia/Kuala_Lumpur';
  if (preTz && (y < 1982 || (y === 1982 && mo === 1 && d === 1))) {
    notes.push({
      code: 'sg_pre_1982_offset',
      text:
        'Singapore and Malaysia moved their clocks to UTC+8 on 1 January 1982. ' +
        'Before that the legal offset was UTC+7:30, which sat closer to the sun ' +
        'overhead at this longitude — so the gap between clock time and true ' +
        'solar time was about half an hour smaller than it is today. Your ' +
        'pillars here are read off the clock time you entered, which is the ' +
        'convention. The note is here so you know the difference exists.',
    });
  }

  if (chart.clock.dayRolled) {
    notes.push({
      code: 'late_zi',
      text:
        'You were born in 子时, the double-hour that opens at 11pm. This page ' +
        'follows 子初换日: the day pillar turns at 11pm, not at midnight. Some ' +
        'schools turn it at midnight instead, which would move your day pillar ' +
        'back one. It is a real disagreement, not a rounding error.',
    });
  }

  return notes;
}

/** The two adjacent charts to show when the birth hour is not certain. */
export function adjacentCharts(input) {
  return [
    { label: 'One hour earlier', chart: computeChart({ ...input, shiftMinutes: -60 }) },
    { label: 'One hour later', chart: computeChart({ ...input, shiftMinutes: 60 }) },
  ];
}

/** Which pillars differ between two charts. */
export function differences(a, b) {
  return ['year', 'month', 'day', 'hour'].filter(
    (k) => a.pillars[k].ganzhi !== b.pillars[k].ganzhi
  );
}

/* ------------------------------------------------------------- the widget */

const PILLAR_LABELS = {
  year: { en: 'Year', zh: '年柱' },
  month: { en: 'Month', zh: '月柱' },
  day: { en: 'Day', zh: '日柱' },
  hour: { en: 'Hour', zh: '时柱' },
};

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function renderPillar(key, block, isDay) {
  const col = el('div', `cr-cast__pillar${isDay ? ' cr-cast__pillar--day' : ''}`);

  const head = el('div', 'cr-cast__pillar-head');
  head.appendChild(el('span', 'cr-cast__pillar-en', PILLAR_LABELS[key].en));
  head.appendChild(el('span', 'cr-cast__pillar-zh', PILLAR_LABELS[key].zh));
  col.appendChild(head);

  const stem = el('div', `cr-cast__glyph cr-cast__glyph--${block.stemElementKey}`, block.stem);
  stem.setAttribute(
    'aria-label',
    `Stem ${block.stem}, ${block.stemElementEn}, ${block.stemYinYang === '阳' ? 'yang' : 'yin'}`
  );
  col.appendChild(stem);
  col.appendChild(
    el('p', 'cr-cast__glyph-note', `${block.stemElementEn} · ${block.stemTenGodEn}`)
  );

  const branch = el(
    'div',
    `cr-cast__glyph cr-cast__glyph--${block.branchElementKey}`,
    block.branch
  );
  branch.setAttribute('aria-label', `Branch ${block.branch}, ${block.branchElementEn}`);
  col.appendChild(branch);
  col.appendChild(el('p', 'cr-cast__glyph-note', block.branchElementEn));

  const hidden = el('ul', 'cr-cast__hidden');
  hidden.setAttribute('role', 'list');
  for (const h of block.hiddenStems) {
    const li = el('li', 'cr-cast__hidden-row');
    const dot = el('span', `cr-cast__dot cr-cast__dot--${h.elementKey}`);
    dot.setAttribute('aria-hidden', 'true');
    li.appendChild(dot);
    li.appendChild(el('span', 'cr-cast__hidden-stem', h.stem));
    li.appendChild(el('span', 'cr-cast__hidden-god', h.tenGodEn));
    hidden.appendChild(li);
  }
  col.appendChild(el('p', 'cr-cast__hidden-label', 'Hidden stems'));
  col.appendChild(hidden);

  return col;
}

function renderPlate(chart) {
  const plate = el('div', 'cr-cast__plate');
  for (const key of chart.order) {
    plate.appendChild(renderPillar(key, chart.pillars[key], key === 'day'));
  }
  return plate;
}

function renderMiniChart(label, chart, diff) {
  const wrap = el('div', 'cr-cast__mini');
  wrap.appendChild(el('p', 'cr-cast__mini-label', label));
  wrap.appendChild(el('p', 'cr-cast__mini-chars', chart.eightCharacters));
  wrap.appendChild(
    el(
      'p',
      'cr-cast__mini-diff',
      diff.length === 0
        ? 'Same chart — this hour sits well inside its double-hour.'
        : `Differs in the ${diff.join(' and ')} ${diff.length > 1 ? 'pillars' : 'pillar'}.`
    )
  );
  return wrap;
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class ChartCast extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-cast-form]');
      this.output = this.querySelector('[data-cast-output]');
      this.errorBox = this.querySelector('[data-cast-error]');
      if (!this.form || !this.output) return;

      const zoneSelect = this.querySelector('[data-cast-timezone]');
      if (zoneSelect && zoneSelect.options.length === 0) {
        for (const tz of TIMEZONES) {
          const opt = document.createElement('option');
          opt.value = tz.id;
          opt.textContent = tz.label;
          if (tz.id === 'Asia/Singapore') opt.selected = true;
          zoneSelect.appendChild(opt);
        }
      }

      this.unsure = this.querySelector('[data-cast-unsure]');
      this.timeField = this.querySelector('[data-cast-time-field]');
      if (this.unsure) {
        this.unsure.addEventListener('change', () => this.syncUnsure());
        this.syncUnsure();
      }

      // No submit, ever. The form element exists for keyboard and label
      // semantics; the birth details never travel anywhere.
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.run();
      });
      const btn = this.querySelector('[data-cast-run]');
      if (btn) btn.addEventListener('click', () => this.run());
    }

    syncUnsure() {
      const on = this.unsure.checked;
      if (this.timeField) this.timeField.classList.toggle('cr-cast__field--soft', on);
    }

    fail(message) {
      this.output.hidden = true;
      this.output.replaceChildren();
      if (this.errorBox) {
        this.errorBox.textContent = message;
        this.errorBox.hidden = false;
      }
    }

    read() {
      const value = (sel) => {
        const node = this.querySelector(sel);
        return node ? node.value : '';
      };
      return {
        date: value('[data-cast-date]'),
        time: value('[data-cast-time]') || '12:00',
        timezone: value('[data-cast-timezone]') || 'Asia/Singapore',
        sex: value('[data-cast-sex]'),
      };
    }

    run() {
      const input = this.read();
      if (!input.date) return this.fail('A birth date is needed to cast the chart.');

      let chart;
      try {
        chart = computeChart(input);
      } catch (e) {
        return this.fail(e.message);
      }
      if (this.errorBox) this.errorBox.hidden = true;

      const uncertain = !!(this.unsure && this.unsure.checked);
      const frag = document.createDocumentFragment();

      const head = el('div', 'cr-cast__result-head');
      head.appendChild(el('p', 'cr-cast__eyebrow', 'Your four pillars'));
      head.appendChild(el('p', 'cr-cast__chars', chart.eightCharacters));
      head.appendChild(
        el(
          'p',
          'cr-cast__daymaster',
          `Day Master ${chart.dayMaster} — ${chart.dayMasterYinYang === '阳' ? 'yang' : 'yin'} ` +
            `${chart.dayMasterElementEn.toLowerCase()}. Month command ${chart.monthCommand.branch}, ` +
            `under ${chart.monthCommand.governingJie}.`
        )
      );
      frag.appendChild(head);
      frag.appendChild(renderPlate(chart));

      const notes = chartNotes(input, chart);
      if (uncertain) {
        notes.unshift({
          code: 'hour_unsure',
          text:
            'You marked the birth time as uncertain. The hour pillar is the one ' +
            'that moves: it changes every two hours, and near a boundary the day ' +
            'pillar can move with it. The two charts below are what an hour ' +
            'either side would give. Nobody should pick between them by seeing ' +
            'which life story fits better — that reasoning proves whatever it ' +
            'set out to prove. A birth certificate or a hospital record settles it.',
        });
      }

      if (chart.luck.known) {
        notes.push({
          code: 'luck_direction',
          text:
            `Your year stem ${chart.luck.yearStem} is ${chart.luck.yearStemPolarity}, so your ` +
            `luck cycles run ${chart.luck.forward ? 'forward' : 'backward'} through the ` +
            'sexagenary sequence. When they open, and what sits in them, is work for a read chart.',
        });
      }

      if (notes.length) {
        const list = el('ul', 'cr-cast__notes');
        list.setAttribute('role', 'list');
        for (const n of notes) list.appendChild(el('li', 'cr-cast__note', n.text));
        frag.appendChild(list);
      }

      if (uncertain) {
        const ab = el('div', 'cr-cast__ab');
        ab.appendChild(el('p', 'cr-cast__ab-label', 'An hour either side'));
        const grid = el('div', 'cr-cast__ab-grid');
        for (const alt of adjacentCharts(input)) {
          grid.appendChild(renderMiniChart(alt.label, alt.chart, differences(chart, alt.chart)));
        }
        ab.appendChild(grid);
        frag.appendChild(ab);
      }

      this.output.replaceChildren(frag);
      this.output.hidden = false;
      this.output.setAttribute('tabindex', '-1');
      this.output.focus({ preventScroll: false });
    }
  }

  if (!customElements.get('chart-cast')) customElements.define('chart-cast', ChartCast);
}
